import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { Form, Input, Radio, Mentions, Spin, message } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { saveGroupAsync, resetGoupSavingState } from "../../redux/boardSlice";
import PrivateIcon from "../../assets/icons/lock.svg";
import PublicIcon from "../../assets/icons/public.svg";
import GlobalIcon from "../../assets/icons/global.svg";
import "./AddBoardGroup.css";

const { Option } = Mentions;
const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;

// Share-list entries are user IDs or emails; emails are matched case-insensitively.
const shareKey = (entry) => String(entry ?? "").trim().toLowerCase();

const dedupeShareList = (list) => {
  const seen = new Set();
  return list.filter((entry) => {
    const key = shareKey(entry);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const AddBoardGroup = (props, ref) => {
  /* Same gate as the Global visibility option below: only a special user sees it.
     Read once here rather than re-reading localStorage at each use site. */
  const isSpecialUser = localStorage.getItem("IsSpecialUser") === "true";

  const [groupName, setGroupName] = useState("");
  const [groupType, setGoupType] = useState(null);
  const [radioVal, setRadioVal] = useState(1);
  const [sharedListShow, setSharedListShow] = useState(true);
  const [usersList, setUsersList] = useState([]);
  const [userIdList, setUserIdList] = useState([]);
  const [subGroupNames, setSubGroupNames] = useState([""]);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState({});
  const [shareWithValue, setShareWithValue] = useState("");
  const [groupNameValidation, setGroupNameValidation] = useState({ status: "", message: "" });
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(null);
  /* True from the keystroke that schedules a duplicate check until its answer
     lands — covers the 800ms debounce as well as the request. Save is blocked
     meanwhile, otherwise a duplicate could be saved before the check says so. */
  const [isNameCheckPending, setIsNameCheckPending] = useState(false);
  // Bumped on every name change; a check whose number is stale is ignored.
  const nameCheckSeqRef = useRef(0);
  /* Whether the last save went out as an update (PUT) or a create (POST) — set at
     the moment it is sent, so the toast names what actually happened. */
  const lastSaveWasUpdateRef = useRef(false);

  const groupNameInputRef = useRef(null);
  const groupNameValidationRef = useRef(groupNameValidation);

  useEffect(() => {
    groupNameValidationRef.current = groupNameValidation;
  }, [groupNameValidation]);

  const [initialValues, setInitialValues] = useState({
    groupName: "",
    groupType: null,
    radioVal: 1,
    sharedListShow: true,
    usersList: [],
    userIdList: [],
    subGroupNames: [""],
    shareWithValue: "",
  });

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const history = useHistory();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* A user picked from the suggestions is saved by user ID, a typed one by email.
     On edit the list comes back as those raw IDs/emails, so checking only the
     displayed emails let an already-shared picked user be added a second time.
     Match on the ID, the email, or the email an ID belongs to. */
  const emailForUserId = (id) =>
    props.users?.find((u) => String(u?.UserID) === String(id))?.UserDetails?.UserName;

  const isAlreadyShared = (email, userId) => {
    const key = shareKey(email);
    return userIdList.some(
      (id, i) =>
        (userId && String(id) === String(userId)) ||
        shareKey(id) === key ||
        shareKey(usersList[i]) === key ||
        shareKey(emailForUserId(id)) === key,
    );
  };

  const resetToInitialState = () => {
    form.resetFields();
    form.setFields([{ name: "groupname", errors: [] }]);
    setGroupName("");
    setGoupType(null);
    setRadioVal(1);
    setSharedListShow(true);
    setUsersList([]);
    setUserIdList([]);
    setSubGroupNames([""]);
    setSelectedGroupDetails({});
    setShareWithValue("");
    setInitialValues({
      groupName: "",
      groupType: null,
      radioVal: 1,
      sharedListShow: true,
      usersList: [],
      userIdList: [],
      subGroupNames: [""],
      shareWithValue: "",
    });
    setGroupNameValidation({ status: "", message: "" });
    setFormSubmitted(false);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
    nameCheckSeqRef.current += 1;
    setIsNameCheckPending(false);
  };

  const resetToEditInitialState = () => {
    setGroupName(initialValues.groupName);
    setGoupType(initialValues.groupType);
    setRadioVal(initialValues.radioVal);
    setSharedListShow(initialValues.sharedListShow);
    setUsersList(initialValues.usersList);
    setUserIdList(initialValues.userIdList);
    setSubGroupNames(initialValues.subGroupNames);
    setShareWithValue(initialValues.shareWithValue);
    form.setFieldsValue({
      groupname: initialValues.groupName,
      grouptype: initialValues.groupType,
      sharewith: initialValues.shareWithValue,
    });
    // the component stays mounted while the modal is closed, so validation errors
    // left over from the last save attempt have to be cleared explicitly
    form.setFields([
      { name: "groupname", errors: [] },
      { name: "grouptype", errors: [] },
      { name: "sharewith", errors: [] },
    ]);
    setGroupNameValidation({ status: "", message: "" });
    setFormSubmitted(false);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
    nameCheckSeqRef.current += 1;
    setIsNameCheckPending(false);
  };

  useImperativeHandle(ref, () => ({ resetToInitialState, resetToEditInitialState }));

  useEffect(() => {
    if (groupNameValidation.status === "duplicate" || groupNameValidation.status === "available") {
      form.validateFields(["groupname"]);
    }
  }, [groupNameValidation]);

  useEffect(() => {
    if (groupNameInputRef.current && cursorPosition !== null) {
      groupNameInputRef.current.input.setSelectionRange(cursorPosition, cursorPosition);
      groupNameInputRef.current.focus();
    }
  }, [groupNameValidation]);

  useEffect(() => {
    return () => {
      if (!props.selectedGroup) resetToInitialState();
    };
  }, []);

  const boardSelector = useSelector((state) => state.boards);

  const onChangeRadio = (e) => {
    const val = e.target.value;
    setRadioVal(val);
    setSharedListShow(val === 1);
  };

  useEffect(() => {
    if (props.selectedGroup) {
      const { title, groupType, isGlobal, isPublic, children } = props.selectedGroup;
      // Groups saved before the duplicate guard may already hold the same user twice.
      const shareWith = dedupeShareList(props.selectedGroup.shareWith || []);
      const newRadioVal = isGlobal ? 3 : isPublic ? 2 : 1;
      const newSharedListShow = newRadioVal === 1;
      setGroupName(title);
      setGoupType(groupType);
      setUserIdList(shareWith || []);
      setUsersList(shareWith || []);
      setRadioVal(newRadioVal);
      setSharedListShow(newSharedListShow);
      const subGroups = children && children.length > 0 ? children.map((c) => c.title) : [""];
      setSubGroupNames(subGroups);
      setInitialValues({
        groupName: title,
        groupType: groupType,
        radioVal: newRadioVal,
        sharedListShow: newSharedListShow,
        usersList: shareWith || [],
        userIdList: shareWith || [],
        subGroupNames: subGroups,
        shareWithValue: "",
      });
      setShareWithValue("");
      setGroupNameValidation({ status: "", message: "" });
      setFormSubmitted(false);
      const existingOrg = props.selectedGroup?.orgID ?? props.selectedGroup?.orgId ?? "";
      form.setFieldsValue({ groupname: title, grouptype: groupType, sharewith: "", organization: existingOrg });
      form.setFields([
        { name: "groupname", errors: [] },
        { name: "grouptype", errors: [] },
        { name: "sharewith", errors: [] },
      ]);
    }
  }, [props.selectedGroup]);

  useEffect(() => {
    // const isUpdate = Boolean(props.selectedGroup?.id);
    const isUpdate = lastSaveWasUpdateRef.current;
    async function saveGroupSuccess() {
      if (boardSelector.groupSavingState === "success") {
        message.success(isUpdate ? "Group updated successfully!" : "Group saved successfully!");
        /* Close first, clear after. Clearing up front (and holding the popup open
           a second) showed the user their form being emptied before it closed. */
        // setGroupName("");
        // setUsersList([]);
        // setUserIdList([]);
        // setShareWithValue("");
        await dispatch(resetGoupSavingState());
        props.handleCloseModal();
        // ~ the antd Modal's close animation, so the reset happens out of sight
        setTimeout(() => {
          resetToInitialState();
          // when opened from a form (eg. Create Dashboard/Report page) we stay on that page
          if (!props.disableRedirect) {
            history.push("/groups");
          }
        }, 300);
      } else if (boardSelector.groupSavingState === "error") {
        // message.error("Group not saved!");
        message.error(isUpdate ? "Group not updated!" : "Group not saved!");
      }
    }
    saveGroupSuccess();
  }, [boardSelector]);

  const onSelectSharableUsers = (option) => {
    const selectedEmail = option.value;
    const selectedUserId = option.key.replace("board_user__", "");
    // if (!usersList.includes(selectedEmail)) {
    if (!isAlreadyShared(selectedEmail, selectedUserId)) {
      setUsersList([...usersList, selectedEmail]);
      setUserIdList([...userIdList, selectedUserId]);
    }
    setShareWithValue("");
    form.setFieldsValue({ sharewith: "" });
  };

  const addGroups = () => setSubGroupNames([...subGroupNames, ""]);

  const setSubGroupName = (val, index) => setSubGroupNames(subGroupNames.map((g, i) => (i === index ? val : g)));

  const removeSubGroup = (id) => setSubGroupNames(subGroupNames.filter((_, i) => id !== i));

  const checkGroupNameDuplicate = async (name) => {
    const seq = nameCheckSeqRef.current;
    if (!name || name.trim() === "" || name.length < 2) {
      setGroupNameValidation({ status: "", message: "" });
      setIsNameCheckPending(false);
      return;
    }
    if (groupNameInputRef.current?.input) {
      setCursorPosition(groupNameInputRef.current.input.selectionStart);
    }
    setGroupNameValidation({ status: "checking", message: "Checking availability..." });
    try {
      const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/check-group-duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ title: name.trim(), id: props.selectedGroup?.id || null }),
      });
      const data = await response.json();
      // The name changed while this was in flight — a newer check owns the result.
      if (seq !== nameCheckSeqRef.current) return;
      if (groupNameInputRef.current?.input) {
        setCursorPosition(groupNameInputRef.current.input.selectionStart);
      }
      setGroupNameValidation(
        data.isDuplicate
          ? { status: "duplicate", message: data.message || "A group with this name already exists" }
          : { status: "available", message: "Group name is available" },
      );
      setIsNameCheckPending(false);
    } catch {
      if (seq !== nameCheckSeqRef.current) return;
      setGroupNameValidation({ status: "error", message: "Error checking group name availability" });
      setIsNameCheckPending(false);
    }
  };

  const saveGroupInfo = async () => {
    // The button is disabled meanwhile; this guards any other path into save.
    if (isNameCheckPending) return;
    setFormSubmitted(true);
    try {
      const values = await form.validateFields();
      let finalSharedWith = [...userIdList];
      if (sharedListShow && shareWithValue && emailRegex.test(shareWithValue)) {
        // if (!usersList.includes(shareWithValue)) finalSharedWith.push(shareWithValue);
        if (!isAlreadyShared(shareWithValue)) finalSharedWith.push(shareWithValue);
      }
      finalSharedWith = dedupeShareList(finalSharedWith);
      let postData = {
        groupName: values.groupname,
        /* The Group Type field is commented out, so there is no form value for it.
           Keep the group's existing type on edit rather than blanking it, and give
           new groups the type the select used to default to. */
        groupType: groupType || "dashboard",
        isPublic: radioVal === 2,
        isGlobal: radioVal === 3,
        createdBySpecial: localStorage.getItem("IsSpecialUser") || false,
        sharedWith: sharedListShow ? finalSharedWith : [],
        subGroups: subGroupNames.filter((sg) => sg.trim() !== ""),
      };

      /* Only sent when a special user actually typed one. Everyone else's org comes off
         the access token on the server, so an empty orgID must not be sent at all rather
         than sent blank. */
      const orgIDValue = (values.organization || "").trim();
      if (isSpecialUser && orgIDValue) {
        postData.orgID = orgIDValue;
      }
      if (props.selectedGroup?.id) {
        postData.id = props.selectedGroup.id;
        postData.isEdit = true;
      }
      lastSaveWasUpdateRef.current = Boolean(postData.isEdit);
      const response = await dispatch(saveGroupAsync(postData));
      if (response.payload.status === "SUCCESS") {
        setGroupNameValidation({ status: "", message: "" });
      }
    } catch (errorInfo) {
      console.log("Validation Failed:", errorInfo);
    }
  };

  const validateShareWith = (_, value) => {
    if (!value) return Promise.resolve();
    if (!emailRegex.test(value)) return Promise.reject(new Error("Please enter a valid email address"));
    // Duplicates used to be dropped silently — say why nothing was added.
    if (isAlreadyShared(value)) return Promise.reject(new Error("This email has already been added"));
    return Promise.resolve();
  };

  const handleShareWithChange = (value) => {
    setShareWithValue(value);
    const fieldError = form.getFieldError("sharewith");
    if (fieldError.length > 0) form.setFields([{ name: "sharewith", errors: [] }]);
  };

  // only used by the commented-out Group Type select
  // const handleChangeGroupType = (val) => {
  //   setGoupType(val);
  //   const fieldError = form.getFieldError("grouptype");
  //   if (fieldError.length > 0) form.setFields([{ name: "grouptype", errors: [] }]);
  // };

  const handleShareWithKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const currentValue = e.target?.value || shareWithValue;
      if (currentValue && !currentValue.startsWith("@") && emailRegex.test(currentValue)) {
        // if (!usersList.includes(currentValue)) {
        if (isAlreadyShared(currentValue)) {
          // Keep the text and show "already added" under the field.
          form.validateFields(["sharewith"]).catch(() => {});
          return;
        }
        setUsersList([...usersList, currentValue]);
        setUserIdList([...userIdList, currentValue]);
        setShareWithValue("");
        form.setFieldsValue({ sharewith: "" });
      }
    }
  };

  const handleShareWithBlur = () => {
    if (shareWithValue && !shareWithValue.startsWith("@") && emailRegex.test(shareWithValue)) {
      // if (!usersList.includes(shareWithValue)) {
      if (isAlreadyShared(shareWithValue)) {
        // Keep the text; the field's onBlur validation shows "already added".
        form.validateFields(["sharewith"]).catch(() => {});
        return;
      }
      setUsersList([...usersList, shareWithValue]);
      setUserIdList([...userIdList, shareWithValue]);
      setShareWithValue("");
      form.setFieldsValue({ sharewith: "" });
    }
  };

  const removeEmail = (index) => {
    setUsersList(usersList.filter((_, i) => i !== index));
    setUserIdList(userIdList.filter((_, i) => i !== index));
  };

  const getNameSuffix = () => {
    if (groupNameValidation.status === "checking") return <Spin size="small" />;
    if (groupNameValidation.status === "available") return <span style={{ color: "#52c41a", fontSize: 16 }}>✓</span>;
    if (groupNameValidation.status === "duplicate") return <span style={{ color: "#ff4d4f", fontSize: 16 }}>⚠</span>;
    return null;
  };

  const isSaving = boardSelector.groupSavingState === "pending";
  /* Save stays disabled until every required field holds a usable value. Group Type
     is no longer one of them — the field is commented out and the visibility radio
     that replaced it always has a value. */
  // No longer gates the Save button — Save now runs validation and shows the errors.
  // const isRequiredDataMissing = (groupName || "").trim().length < 2 || groupNameValidation.status === "duplicate";

  return (
    <div className="abg-modal-body">
      <Form name="add_board_group" initialValues={selectedGroupDetails} form={form} layout="horizontal">
        {/* Group Name */}
        <div className="abg-form-row">
          <label className="abg-label">
            Group Name <span className="abg-required">*</span>
          </label>
          <div className="abg-field">
            <Form.Item
              name="groupname"
              /* No trigger until the first Save click — validating onBlur made the
                 "required" error pop up on any click inside the popup. After a save
                 attempt it re-validates live so the error clears as the user types. */
              // validateTrigger={formSubmitted ? ["onChange", "onBlur"] : "onBlur"}
              validateTrigger={formSubmitted ? ["onChange", "onBlur"] : []}
              rules={[
                { required: true, message: "Please input group name!" },
                { min: 2, message: "Group name must be at least 2 characters!" },
                {
                  validator: () =>
                    groupNameValidationRef.current.status === "duplicate"
                      ? Promise.reject(new Error(groupNameValidationRef.current.message))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                ref={groupNameInputRef}
                className="abg-input"
                placeholder="Enter group name"
                disabled={props.isViewOnly}
                suffix={getNameSuffix()}
                onChange={(e) => {
                  const newName = e.target.value;
                  setGroupName(newName);
                  setCursorPosition(e.target.selectionStart);
                  if (groupNameValidation.status !== "") setGroupNameValidation({ status: "", message: "" });
                  if (debounceTimer) clearTimeout(debounceTimer);
                  nameCheckSeqRef.current += 1;
                  if (newName.trim().length >= 2) {
                    setIsNameCheckPending(true);
                    setDebounceTimer(setTimeout(() => checkGroupNameDuplicate(newName), 800));
                  } else {
                    setIsNameCheckPending(false);
                  }
                }}
                onKeyDown={(e) => setCursorPosition(e.target.selectionStart)}
                onClick={(e) => setCursorPosition(e.target.selectionStart)}
              />
            </Form.Item>
          </div>
        </div>

        {/* Group Type (Dashboard / Report) — not wanted at group level. Its label now
            sits on the visibility row below, which took this row's place. */}
        {/* <div className="abg-form-row">
          <label className="abg-label">
            Group Type <span className="abg-required">*</span>
          </label>
          <div className="abg-field">
            <Form.Item name="grouptype" rules={[{ required: true, message: "Please select group type!" }]}>
              <Select
                className="abg-select"
                placeholder="Dashboard"
                value={groupType}
                onChange={handleChangeGroupType}
                disabled={props.isViewOnly}
              >
                <Select.Option value="dashboard">Dashboard</Select.Option>
                <Select.Option value="report">Report</Select.Option>
              </Select>
            </Form.Item>
          </div>
        </div> */}

        {/* Group Type is now the visibility choice: Private / Public (/ Global) */}
        <div className="abg-form-row abg-form-row--radio">
          <label className="abg-label">
            Group Type <span className="abg-required">*</span>
          </label>
          <div className="abg-field">
            <Radio.Group
              value={radioVal}
              onChange={onChangeRadio}
              disabled={props.isViewOnly}
              className="abg-radio-group"
            >
              <Radio value={1}>
                <span className="abg-radio-label">
                  {/* <MaterialDesignIcon icon="fingerprint" /> */}
                  <img src={PrivateIcon} alt="Private" style={{ width: 18, height: 18 }} />
                  &nbsp;Private
                </span>
              </Radio>
              <Radio value={2}>
                <span className="abg-radio-label">
                  {/* <MaterialDesignIcon icon="groups" /> */}
                  <img src={PublicIcon} alt="Public" style={{ width: 18, height: 18 }} />
                  &nbsp;Public
                </span>
              </Radio>
              {localStorage.getItem("IsSpecialUser") === "true" && (
                <Radio value={3}>
                  <span className="abg-radio-label">
                    {/* <MaterialDesignIcon icon="public" /> */}
                    <img src={GlobalIcon} alt="Global" style={{ width: 18, height: 18 }} />
                    &nbsp;Global
                  </span>
                </Radio>
              )}
            </Radio.Group>
          </div>
        </div>

        {/* Organization — special users only, like the Global option above. Everyone
            else's group belongs to the org on their token, so there is nothing to ask. */}
        {isSpecialUser && (
          <div className="abg-form-row">
            <label className="abg-label">Organization</label>
            <div className="abg-field">
              <Form.Item name="organization">
                <Input className="abg-input" placeholder="Enter organization ID" disabled={props.isViewOnly} />
              </Form.Item>
            </div>
          </div>
        )}

        {/* Share With */}
        {sharedListShow && (
          <div className="abg-form-row abg-form-row--sharewith">
            <label className="abg-label">Share With</label>
            <div className="abg-field">
              {!props.isViewOnly ? (
                <>
                  <Form.Item
                    name="sharewith"
                    rules={[{ validator: validateShareWith }]}
                    validateTrigger={["onBlur", "onSubmit"]}
                  >
                    <Mentions
                      className="abg-input"
                      value={shareWithValue}
                      onChange={handleShareWithChange}
                      onSelect={onSelectSharableUsers}
                      onKeyDown={handleShareWithKeyPress}
                      onBlur={handleShareWithBlur}
                      placeholder="Eg. abc@example.com"
                      prefix=""
                      notFoundContent="No users found"
                      dropdownClassName="abg-mentions-dropdown"
                    >
                      {props.users && shareWithValue.length >= 1
                        ? props.users
                            .filter(
                              (u) =>
                                // !usersList.includes(u?.UserDetails?.UserName) &&
                                !isAlreadyShared(u?.UserDetails?.UserName, u?.UserID) &&
                                u?.UserDetails?.UserName?.toLowerCase().includes(shareWithValue.toLowerCase()),
                            )
                            .map((u) => (
                              <Option key={`board_user__${u.UserID}`} data={u} value={u?.UserDetails?.UserName}>
                                {u?.UserDetails?.UserName || "-"}
                              </Option>
                            ))
                        : null}
                    </Mentions>
                  </Form.Item>
                  {usersList.length > 0 && (
                    <div className="abg-tags">
                      {usersList.map((email, i) => (
                        <span key={i} className="abg-tag">
                          {/* on edit a picked user comes back as a user ID — show the email */}
                          {emailForUserId(email) || email}
                          <CloseOutlined className="abg-tag-remove" onClick={() => removeEmail(i)} />
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : usersList.length > 0 ? (
                <div className="abg-tags">
                  {usersList.map((email, i) => (
                    <span key={i} className="abg-tag abg-tag--readonly">
                      {emailForUserId(email) || email}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="abg-empty-text">No shared users</span>
              )}
            </div>
          </div>
        )}

        {/* Sub Groups */}
        <div className="abg-form-row abg-form-row--subgroups">
          <label className="abg-label">Sub groups</label>
          <div className="abg-field abg-subgroups">
            {props.isViewOnly ? (
              subGroupNames.filter((sg) => sg.trim()).length > 0 ? (
                <div className="abg-tags">
                  {subGroupNames
                    .filter((sg) => sg.trim())
                    .map((sg, i) => (
                      <span key={i} className="abg-tag abg-tag--readonly">
                        {sg}
                      </span>
                    ))}
                </div>
              ) : (
                <span className="abg-empty-text">No sub groups</span>
              )
            ) : (
              <>
                {/* First row — always has + button; gets × too when more rows exist */}
                <div className="abg-subgroup-row">
                  <Input
                    className="abg-input abg-subgroup-input"
                    placeholder="Enter sub group name"
                    value={subGroupNames[0]}
                    onChange={(e) => setSubGroupName(e.target.value, 0)}
                  />
                  {subGroupNames.length > 1 && (
                    <button
                      type="button"
                      className="abg-subgroup-remove"
                      onClick={() => removeSubGroup(0)}
                      aria-label="Remove sub group"
                    >
                      <CloseOutlined />
                    </button>
                  )}
                  <button type="button" className="abg-add-subgroup-btn" onClick={addGroups} aria-label="Add sub group">
                    <PlusOutlined />
                  </button>
                </div>

                {/* Additional rows — only × */}
                {subGroupNames.slice(1).map((sg, i) => {
                  const index = i + 1;
                  return (
                    <div key={"sg_" + index} className="abg-subgroup-row">
                      <Input
                        className="abg-input abg-subgroup-input"
                        placeholder="Enter sub group name"
                        value={sg}
                        onChange={(e) => setSubGroupName(e.target.value, index)}
                      />
                      <button
                        type="button"
                        className="abg-subgroup-remove"
                        onClick={() => removeSubGroup(index)}
                        aria-label="Remove sub group"
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        {!props.isViewOnly && (
          <div className="abg-footer">
            {/* call with no args — the click event must never reach the parent's close handler */}
            <button type="button" className="abg-btn abg-btn--cancel" onClick={() => props.handleCloseModal()}>
              Cancel
            </button>
            <button
              type="button"
              className="abg-btn abg-btn--primary"
              onClick={saveGroupInfo}
              /* Not disabled on missing data any more: Save is where the required-field
                 errors are shown, so it has to be clickable with an empty name. */
              // disabled={isSaving || isRequiredDataMissing}
              disabled={isSaving || isNameCheckPending}
            >
              {/* {isSaving ? <Spin size="small" /> : "Save Group"} */}
              {/* An existing group (has an id — the same test saveGroupInfo uses to
                  send an update) is being edited, so the button says so. */}
              {isSaving ? <Spin size="small" /> : props.selectedGroup?.id ? "Update Group" : "Save Group"}
            </button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default forwardRef(AddBoardGroup);
